import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CheckIcon, RevertIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import type { MapMigratedDataTableMeta } from "../MapMigratedDataTaskBody";
import styles from "../MapMigratedDataTaskBody.module.scss";

export const ActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ImportedAccount, string>) => {
        const account = row.original;
        const meta = table.options.meta as MapMigratedDataTableMeta | undefined;

        const isClosed = account.isClosed;
        const isFinalized = account.financialAccountMappingFinishedAt != null;

        const handleMarkAsClosed = useCallback(() => {
            meta?.onMarkAsClosed({ importedAccountId: account.id });
        }, [meta, account.id]);

        const handleUndoClose = useCallback(() => {
            meta?.onUndoClose({ importedAccountId: account.id });
        }, [meta, account.id]);

        if (isClosed) {
            if (isFinalized) {
                return <ViewTableCell />;
            }
            return (
                <ViewTableCell>
                    <div className={styles.actionsCell}>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleUndoClose}
                            className={viewTableStyles.showOnHover}
                            iconOnly
                            aria-label="Revert closed account"
                        >
                            <RevertIcon />
                        </Button>
                    </div>
                </ViewTableCell>
            );
        }

        if (isFinalized) {
            return (
                <ViewTableCell>
                    <div className={styles.actionsCell}>
                        <CheckIcon width={16} height={16} />
                    </div>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <div className={styles.actionsCell}>
                    {account.mappedFinancialAccountId != null && (
                        <CheckIcon width={16} height={16} />
                    )}
                    <Menu.More className={viewTableStyles.showOnHover}>
                        <Menu.Item onClick={handleMarkAsClosed}>
                            Mark as closed
                        </Menu.Item>
                    </Menu.More>
                </div>
            </ViewTableCell>
        );
    },
);
ActionsCell.displayName = "ActionsCell";
